<template>
    <div
        class="flex"
        :class="{
            'border-b border-b-blue-500': highlighted && dateFormatted,
        }"
    >
        <input
            class="form-control | flex-1"
            type="text"
            :class="[classes, { 'attached-r': showIcon }]"
            :disabled="disabled"
            :placeholder="placeholder"
            :required="required"
            :value="dateFormatted"
            ref="input"
            @change="updateDate"
        />
        <slot name="before-button" />
        <activix-tooltip :content="iconTooltip">
            <activix-button
                class="attached-l"
                :data-testid="buttonTestId"
                :class="buttonClass"
                :size="buttonSize"
                :has-error="hasError"
                :disabled="disabled"
                @click="showPicker"
                v-if="showIcon"
            >
                <icon :name="icon" />
            </activix-button>
        </activix-tooltip>
        <slot name="after-button" />
    </div>
</template>

<script>
    import { sizes as btnSizes } from '@/components/elements/ActivixButton.vue';
    import { getCurrentLocale } from '@/value-objects/dateEnvironment.ts';
    import Moment from '../../value-objects/Moment.js';

    export default {
        compatConfig: { COMPONENT_V_MODEL: false },

        emits: ['update:modelValue'],

        props: {
            container: {
                type: String,
                default: 'body',
            },
            dateOnly: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            endDate: {
                type: String,
                default: '',
            },
            hasError: {
                type: Boolean,
                default: false,
            },
            initialFocus: {
                type: Boolean,
                default: false,
            },
            buttonClass: {
                type: [String, Array],
                default: '',
            },
            buttonSize: {
                type: String,
                default: 'medium',
                validator: (value) => Object.keys(btnSizes).includes(value),
            },
            buttonTestId: {
                type: String,
                default: null,
            },
            icon: {
                type: String,
                default: 'regular/calendar-3',
            },
            inputClass: {
                type: [String, Array],
                default: '',
            },
            pickerPosition: {
                type: String,
                default: 'bottom-right',
            },
            placeholder: {
                type: String,
                default: '',
            },
            required: {
                type: Boolean,
                default: false,
            },
            startDate: {
                type: String,
                default: '',
            },
            startView: {
                type: Number,
                default: 2,
            },
            modelValue: {
                type: String,
                default: '',
            },
            highlighted: {
                type: Boolean,
                default: false,
            },
            showIcon: {
                type: Boolean,
                default: true,
            },
            iconTooltip: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                date: '',
            };
        },

        computed: {
            classes() {
                const inputClass = Array.isArray(this.inputClass)
                    ? this.inputClass
                    : [this.inputClass];

                return inputClass.concat([{ 'has-error': this.hasError }]);
            },

            dateFunction() {
                return this.dateOnly ? this.localeDate : this.locale_dt;
            },

            dateType() {
                return this.dateOnly ? 'date' : 'dateTime';
            },

            componentLocale() {
                return getCurrentLocale(this.$i18n.getLocale());
            },

            isFrenchLocale() {
                return this.componentLocale === 'fr';
            },

            displayFormat() {
                return Moment.humanShortLocalizedFormat(this.dateOnly, this.$i18n.getLocale());
            },

            dateFormatted() {
                return this.formatDateForPicker(this.date);
            },

            endDateFormatted() {
                return this.formatDateForPicker(this.endDate);
            },

            startDateFormatted() {
                return this.formatDateForPicker(this.startDate);
            },

            pluginFormat() {
                if (this.dateOnly) {
                    return this.isFrenchLocale ? 'dd M yyyy' : 'M dd, yyyy';
                }

                return this.isFrenchLocale ? 'dd M yyyy hh:ii' : 'M dd, yyyy H:ii p';
            },

            // Matches datetimepicker output: 24h clock with meridiem (e.g. "14:30 PM").
            pluginMomentFormat() {
                if (this.dateOnly) {
                    return this.isFrenchLocale
                        ? Moment.DATE_HUMAN_SHORT_FORMAT
                        : Moment.DATE_HUMAN_SHORT_MERIDIEM_FORMAT;
                }

                return this.isFrenchLocale
                    ? Moment.DATETIME_HUMAN_SHORT_FORMAT
                    : Moment.DATETIME_HUMAN_SHORT_24H_MERIDIEM_FORMAT;
            },

            datePickerMonthsShort() {
                return (
                    $.fn?.datetimepicker?.dates?.[this.$i18n.getContentLanguage()]?.monthsShort ||
                    []
                );
            },
        },

        watch: {
            startDateFormatted() {
                if (this.startDateFormatted) {
                    $(this.$refs.input).datetimepicker('setStartDate', this.startDateFormatted);
                }
            },

            endDateFormatted() {
                if (this.endDateFormatted) {
                    $(this.$refs.input).datetimepicker('setEndDate', this.endDateFormatted);
                }
            },

            pluginFormat() {
                this.resetPicker();
            },

            modelValue() {
                this.setDate();
            },

            startDate() {
                this.resetPicker();
            },
        },

        methods: {
            formatDateForPicker(date) {
                const formattedDate = this.dateFunction(date);

                if (!formattedDate.isValid()) {
                    return '';
                }

                if (!this.isFrenchLocale) {
                    return formattedDate.display(this.displayFormat, this.$i18n.getLocale());
                }

                const month =
                    $.fn?.datetimepicker?.dates?.[this.$i18n.getContentLanguage()]?.monthsShort?.[
                        Number(formattedDate.format('M')) - 1
                    ];

                if (!month) {
                    return formattedDate.display(this.displayFormat, this.$i18n.getLocale());
                }

                const dateText = `${formattedDate.format('DD')} ${month} ${formattedDate.format(
                    'YYYY'
                )}`;

                if (this.dateOnly) {
                    return dateText;
                }

                return `${dateText} ${formattedDate.format('HH:mm')}`;
            },

            setDate() {
                this.date = this.dateFunction(this.modelValue).toString();
            },

            parseInputDate(inputValue) {
                const date = this.parseInputDateWithFormats(inputValue, [
                    this.displayFormat,
                    this.pluginMomentFormat,
                ]);

                // FR has an extra fallback: the datetimepicker plugin uses custom month
                // names that differ from moment's locale. EN doesn't have this problem.
                if (date.isValid() || !this.isFrenchLocale) {
                    return date;
                }

                return this.parseInputDateWithDatePickerMonth(inputValue);
            },

            /**
             * Try each format until one parses successfully.
             * @param {string} inputValue - Raw text from the input field.
             * @param {string[]} formats - Moment format strings to try in order.
             * @returns {Moment} First valid Moment, or the last parsed result (even if invalid).
             */
            parseInputDateWithFormats(inputValue, formats) {
                const uniqueFormats = [...new Set(formats)];
                let fallbackDate;

                for (const format of uniqueFormats) {
                    const parsedDate = Moment.init(inputValue, this.dateType, {
                        format,
                        locale: this.$i18n.getLocale(),
                    });

                    if (parsedDate.isValid()) {
                        return parsedDate;
                    }

                    fallbackDate = parsedDate;
                }

                return fallbackDate;
            },

            /**
             * Parse input that contains datetimepicker's custom month names.
             *
             * The datetimepicker plugin uses its own month abbreviations (e.g. "mai-picker")
             * that moment.js doesn't recognize, even with locale set to French.
             * Moment's MMM token only matches its own locale month names.
             *
             * Workaround: replace the month name with its numeric index ("05"),
             * then parse with a numeric format ("DD MM YYYY HH:mm").
             * This bypasses moment's locale-based month matching entirely.
             *
             * @param {string} inputValue - Raw text from the input field.
             * @returns {Moment} Parsed result (may be invalid if no month matched).
             */
            parseInputDateWithDatePickerMonth(inputValue) {
                const monthIndex = this.datePickerMonthsShort.findIndex(
                    (month) => month && inputValue.includes(month)
                );

                // No known month name found. Return a parse result anyway so the
                // caller can check .isValid() and clear the field if needed.
                if (monthIndex < 0) {
                    return Moment.init(inputValue, this.dateType, {
                        format: this.displayFormat,
                        locale: this.$i18n.getLocale(),
                    });
                }

                const month = this.datePickerMonthsShort[monthIndex];
                const monthNumber = String(monthIndex + 1).padStart(2, '0');
                const normalizedInputValue = inputValue.replace(month, monthNumber);
                const normalizedFormat = this.dateOnly ? 'DD MM YYYY' : 'DD MM YYYY HH:mm';

                return Moment.init(normalizedInputValue, this.dateType, {
                    format: normalizedFormat,
                    locale: this.$i18n.getLocale(),
                });
            },

            initPicker() {
                this.setDate();

                $(this.$refs.input)
                    .datetimepicker({
                        minHour: 6,
                        autoclose: true,
                        container: this.container,
                        endDate: this.endDateFormatted,
                        format: this.pluginFormat,
                        initialDate: this.dateFormatted,
                        language: this.$i18n.getContentLanguage(),
                        pickerPosition: this.pickerPosition,
                        startDate: this.startDateFormatted,
                        startView: this.startView,
                        todayBtn: true,
                    })
                    .on('changeDate', (event) => {
                        this.updateDate(event.date);
                    });

                if (this.initialFocus && this.$refs.input) {
                    this.$refs.input.focus();
                }
            },

            resetPicker() {
                $(this.$refs.input).datetimepicker('remove');
                this.initPicker();
            },

            updateDate(pickerDate) {
                let date;

                if (pickerDate instanceof Date) {
                    const y = pickerDate.getFullYear();
                    const m = String(pickerDate.getMonth() + 1).padStart(2, '0');
                    const d = String(pickerDate.getDate()).padStart(2, '0');

                    if (this.dateOnly) {
                        date = this.dateFunction(`${y}-${m}-${d}`, 'YYYY-MM-DD');
                    } else {
                        const h = String(pickerDate.getHours()).padStart(2, '0');
                        const min = String(pickerDate.getMinutes()).padStart(2, '0');
                        date = this.dateFunction(`${y}-${m}-${d} ${h}:${min}`, 'YYYY-MM-DD HH:mm');
                    }
                } else {
                    date = this.parseInputDate(this.$refs.input.value);
                }

                if (date.isValid()) {
                    this.date = date.toString();
                    this.$emit('update:modelValue', this.date);

                    return;
                }

                this.$refs.input.value = '';
                this.date = '';
                this.$emit('update:modelValue', '');
            },

            showPicker() {
                $(this.$refs.input).datetimepicker('show');
            },
        },

        mounted() {
            this.initPicker();
        },

        beforeUnmount() {
            $(this.$refs.input).datetimepicker('remove');
        },
    };
</script>

<style lang="less">
    .datetimepicker {
        .datetimepicker-hours {
            max-height: 270px;
            overflow: auto;
        }
    }
</style>
